/**
 * EntityInputElement - Inline combobox for multi-entity mention autocomplete.
 *
 * Uses Plate's InlineCombobox pattern with useEntitySearch for searching
 * users, contacts, companies, and deals.
 */

import { SearchLg } from '@untitledui/icons';
import type { TElement } from 'platejs';
import { KEYS } from 'platejs';
import type { PlateElementProps } from 'platejs/react';
import { useEditorRef } from 'platejs/react';
import { useCallback, useMemo, useState } from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import {
  InlineCombobox,
  InlineComboboxContent,
  InlineComboboxEmpty,
  InlineComboboxGroup,
  InlineComboboxGroupLabel,
  InlineComboboxInput,
  InlineComboboxItem,
} from '@/lib/crm/components/inline-combobox';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import {
  ENTITY_TYPE_TO_RESULT_KEY,
  type EntityResult,
  type EntityType,
  useEntitySearch,
} from '@/lib/search';

const MAX_RESULTS_PER_TYPE = 10;
const MIN_QUERY_LENGTH = 2;

// Display order for grouped result sections. Static — hoisted to module scope
// so it keeps a stable reference across renders (FRONTEND_PATTERNS "Hoist
// Constants").
const ENTITY_TYPE_ORDER: EntityType[] = [
  'ahoy_user',
  'ahoy_contact',
  'ahoy_company',
  'ahoy_deal',
];

interface EntityInputElementProps extends PlateElementProps {
  entityTypes?: EntityType[];
}

/**
 * Get section label for entity type group.
 */
function getSectionLabel(type: EntityType): string {
  switch (type) {
    case 'ahoy_user':
      return 'Users';
    case 'ahoy_contact':
      return 'Contacts';
    case 'ahoy_company':
      return 'Companies';
    case 'ahoy_deal':
      return 'Deals';
    default:
      return type;
  }
}

interface EntityComboboxItemProps {
  entity: EntityResult;
  onSelect: (entity: EntityResult) => void;
}

/**
 * A single selectable entity row inside the mention combobox. Extracted from
 * the result map so it has a stable component identity and its click handler is
 * a stable `useCallback` rather than an inline arrow (FRONTEND_PATTERNS
 * "Extract Components Instead of Inline Render Functions" / "Stable
 * References"). Mirrors `EntityChip` in EntityTypeahead.
 */
function EntityComboboxItem({ entity, onSelect }: EntityComboboxItemProps) {
  const Icon = getEntityTypeIconComponent(entity.type);

  const handleClick = useCallback(() => {
    onSelect(entity);
  }, [entity, onSelect]);

  const keywords = useMemo(
    () => [entity.secondary_text].filter(Boolean),
    [entity.secondary_text]
  );

  return (
    <InlineComboboxItem
      value={entity.name}
      keywords={keywords}
      onClick={handleClick}
      className='flex items-center gap-2 h-auto py-2'
    >
      {entity.avatar_url ? (
        <Avatar size='xs' src={entity.avatar_url} alt={entity.name} />
      ) : (
        <Icon className='size-6 text-fg-quaternary' />
      )}
      <div className='flex flex-col min-w-0 flex-1'>
        <span className='text-sm font-medium text-primary truncate'>
          {entity.name}
        </span>
        {entity.secondary_text && (
          <span className='text-xs text-tertiary truncate'>
            {entity.secondary_text}
          </span>
        )}
      </div>
    </InlineComboboxItem>
  );
}

/**
 * EntityInputElement - Renders the mention input with inline combobox
 * for searching and selecting entities.
 */
export function EntityInputElement({
  attributes,
  children,
  element,
  entityTypes,
}: EntityInputElementProps) {
  const editor = useEditorRef();
  const trigger = (element as { trigger?: string }).trigger || '@';

  const [rawQuery, setRawQuery] = useState('');

  // Use entity search (debouncing is handled inside useEntitySearch)
  const { results, isLoading, error } = useEntitySearch(rawQuery, {
    types: entityTypes,
    enabled: rawQuery.length >= MIN_QUERY_LENGTH,
  });

  // Group and limit results - memoized to avoid recalculation
  const groupedResults = useMemo(() => {
    if (!results) {
      return [];
    }

    const groups: { type: EntityType; items: EntityResult[] }[] = [];

    for (const type of ENTITY_TYPE_ORDER) {
      // Skip types not in entityTypes filter
      if (entityTypes && !entityTypes.includes(type)) {
        continue;
      }

      const key = ENTITY_TYPE_TO_RESULT_KEY[type] as keyof typeof results;
      const items = key ? results[key] || [] : [];
      if (items.length > 0) {
        // Limit to MAX_RESULTS_PER_TYPE per type
        groups.push({ type, items: items.slice(0, MAX_RESULTS_PER_TYPE) });
      }
    }

    return groups;
  }, [results, entityTypes]);

  // Flatten results for determining if we have any results
  const hasResults = useMemo(() => {
    return groupedResults.some(g => g.items.length > 0);
  }, [groupedResults]);

  // Handle selecting an entity
  const handleSelectEntity = useCallback(
    (entity: EntityResult) => {
      // Insert entity element with all required data
      editor.tf.insertNodes({
        type: KEYS.mention,
        value: entity.entity_id,
        displayName: entity.name,
        entityType: entity.type,
        children: [{ text: '' }],
      } as TElement);
      editor.tf.move({ unit: 'offset' });
    },
    [editor]
  );

  const showQueryTooShort =
    rawQuery.length > 0 && rawQuery.length < MIN_QUERY_LENGTH;
  const showLoading = rawQuery.length >= MIN_QUERY_LENGTH && isLoading;
  const showError = rawQuery.length >= MIN_QUERY_LENGTH && error && !isLoading;
  const showNoResults =
    rawQuery.length >= MIN_QUERY_LENGTH && !isLoading && !error && !hasResults;
  const showResults =
    rawQuery.length >= MIN_QUERY_LENGTH && !isLoading && !error && hasResults;

  return (
    <span {...attributes}>
      <InlineCombobox
        element={element}
        trigger={trigger}
        showTrigger
        filter={false}
        value={rawQuery}
        setValue={setRawQuery}
      >
        <InlineComboboxInput className='bg-utility-brand-50 text-utility-brand-700 rounded px-0.5' />
        <InlineComboboxContent className='bg-primary border border-primary rounded-xl shadow-lg max-h-72 overflow-y-auto min-w-[320px]'>
          {/* Query too short */}
          {showQueryTooShort && (
            <InlineComboboxEmpty className='text-tertiary px-3 py-4 text-sm text-center'>
              Keep typing to search...
            </InlineComboboxEmpty>
          )}

          {/* Loading state — wrapped in InlineComboboxEmpty so hasEmpty flips true and the popover stays open during the fetch. */}
          {showLoading && (
            <InlineComboboxEmpty className='flex items-center justify-center px-3 py-4'>
              <LoadingIndicator size='xs' label='Searching…' />
            </InlineComboboxEmpty>
          )}

          {/* Error state */}
          {showError && (
            <InlineComboboxEmpty className='text-tertiary px-3 py-4 text-sm text-center'>
              {error?.message || 'Search failed'}
            </InlineComboboxEmpty>
          )}

          {/* No results */}
          {showNoResults && (
            <InlineComboboxEmpty className='text-tertiary px-3 py-4 text-sm text-center'>
              No results for &apos;{rawQuery}&apos;
            </InlineComboboxEmpty>
          )}

          {/* Results grouped by type */}
          {showResults &&
            groupedResults.map(({ type, items }) => (
              <InlineComboboxGroup key={type}>
                <InlineComboboxGroupLabel>
                  {getSectionLabel(type)}
                </InlineComboboxGroupLabel>
                {items.map(entity => (
                  <EntityComboboxItem
                    key={`${entity.type}:${entity.id}`}
                    entity={entity}
                    onSelect={handleSelectEntity}
                  />
                ))}
              </InlineComboboxGroup>
            ))}

          {/* Initial empty state - prompt to type */}
          {rawQuery.length === 0 && (
            <InlineComboboxEmpty className='px-3 py-2.5'>
              <div className='flex items-center gap-2.5'>
                <div className='flex items-center justify-center size-8 rounded-full bg-gray-100 shrink-0'>
                  <SearchLg className='size-4 text-gray-400' />
                </div>
                <div className='flex flex-col text-left'>
                  <span className='text-sm font-medium text-primary'>
                    Search to mention
                  </span>
                  <span className='text-xs text-tertiary'>
                    Users, contacts, companies, deals
                  </span>
                </div>
              </div>
            </InlineComboboxEmpty>
          )}
        </InlineComboboxContent>
      </InlineCombobox>
      {children}
    </span>
  );
}

/**
 * Factory function to create an EntityInputElement with specific entity types.
 * This is needed because Plate's plugin component binding doesn't support passing props.
 */
// eslint-disable-next-line react-refresh/only-export-components -- factory function co-located with component
export function createEntityInputElement(entityTypes?: EntityType[]) {
  return function EntityInputElementWrapper(props: PlateElementProps) {
    return <EntityInputElement {...props} entityTypes={entityTypes} />;
  };
}
